import { createSearchParams } from 'react-router-dom';

import { JmsManagementRoutes } from '@/pages/JMS/Managament/utils/utils';
import { TabName, TabStatus } from '@/pages/JMS/store/BookingStore';
import { MainRoutes } from '@/routes/RoutePaths';

export const getTabGroups = (
  bookingId: string | undefined,
  inmateId: string | undefined,
  currentPath: string,
  submittedData?: Partial<Record<TabName, { status: TabStatus }>>,
  isViewMode?: boolean,
  isInmateContext?: boolean,
) => {
  const isIdentitySubmitted =
    submittedData?.identity?.status === TabStatus.SUBMITTED;
  const isBookingSubmitted =
    submittedData?.details?.status === TabStatus.SUBMITTED;

  const withQueryParams = (path: string) => {
    const paramMap = {
      mode: isViewMode ? 'view' : undefined,
      context: isInmateContext ? 'inmate' : undefined,
    };

    const params = Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(paramMap).filter(([_, value]) => value !== undefined),
    ) as Record<string, string>;

    if (Object.keys(params).length === 0) {
      return path;
    }

    return `${path}?${createSearchParams(params)}`;
  };

  return [
    {
      matchRoutes: [JmsManagementRoutes.SearchPerson],
      config: {
        headerTitle: 'Book an inmate',
        tabs: [],
      },
    },
    {
      matchRoutes: [
        JmsManagementRoutes.CreateIdentity,
        JmsManagementRoutes.CreateBooking,
        JmsManagementRoutes.EditIdentity(bookingId),
        JmsManagementRoutes.EditBooking(bookingId),
        JmsManagementRoutes.EditInmateContextIdentity(inmateId),
        JmsManagementRoutes.ViewInmateContextIdentity(inmateId),
        JmsManagementRoutes.ViewInmateContextHousing(inmateId),
        JmsManagementRoutes.EditInmateContextHousing(inmateId),
        JmsManagementRoutes.EditInmateContextNotes(inmateId),
        JmsManagementRoutes.ViewInmateContextBookingHistory(inmateId),
        JmsManagementRoutes.ViewInmateContextIdentity(inmateId),
        JmsManagementRoutes.EditFingerprints(bookingId),
        JmsManagementRoutes.EditHousing(bookingId),
        JmsManagementRoutes.EditProperties(bookingId),
      ],
      config: {
        headerTitle: !isViewMode && !isInmateContext ? 'Book an inmate' : '',
        tabs: [
          {
            title: 'Identity',
            value: TabName.IDENTITY,
            active: ['search', 'form', 'identity'].includes(currentPath),
            navigateTo: bookingId
              ? withQueryParams(JmsManagementRoutes.EditIdentity(bookingId))
              : inmateId
              ? withQueryParams(
                  JmsManagementRoutes.EditInmateContextIdentity(inmateId),
                )
              : JmsManagementRoutes.CreateIdentity,
            disabled: false,
          },
          {
            title: 'Booking History',
            value: TabName.BOOKING_HISTORY,
            active: currentPath === 'booking-history',
            navigateTo: withQueryParams(
              JmsManagementRoutes.ViewInmateContextBookingHistory(inmateId),
            ),
            hidden: !isInmateContext,
          },
          {
            title: 'Housing',
            value: TabName.HOUSING,
            active: currentPath === 'housing',
            navigateTo: withQueryParams(
              JmsManagementRoutes.EditInmateContextHousing(inmateId),
            ),
            hidden: !isInmateContext,
          },
          {
            title: 'Notes',
            value: TabName.NOTES,
            active: currentPath === 'notes',
            navigateTo: withQueryParams(
              JmsManagementRoutes.EditInmateContextNotes(inmateId),
            ),
            hidden: !isInmateContext,
          },
          {
            title: 'Booking',
            value: TabName.DETAILS,
            active: currentPath === 'booking',
            navigateTo: !bookingId
              ? JmsManagementRoutes.CreateBooking
              : withQueryParams(JmsManagementRoutes.EditBooking(bookingId)),
            disabled: !isIdentitySubmitted,
            hidden: isInmateContext,
          },
          {
            title: 'Fingerprints',
            value: TabName.FINGERPRINTS,
            active: currentPath === 'fingerprints',
            navigateTo: withQueryParams(
              JmsManagementRoutes.EditFingerprints(bookingId),
            ),
            disabled: !isBookingSubmitted,
            hidden: !isBookingSubmitted || isInmateContext,
          },
          {
            title: 'Properties',
            value: TabName.PROPERTIES,
            active: currentPath === 'properties',
            navigateTo: withQueryParams(
              JmsManagementRoutes.EditProperties(bookingId),
            ),
            disabled: !isBookingSubmitted,
            hidden: !isBookingSubmitted || isInmateContext,
          },
          {
            title: 'Medical Info',
            value: TabName.MEDICAL,
            active: currentPath === 'medical',
            navigateTo: withQueryParams(
              `${MainRoutes.JmsManagement}/booking/${bookingId}/medical`,
            ),
            disabled: !isBookingSubmitted,
            hidden: !isBookingSubmitted || isInmateContext,
          },
          {
            title: 'Risk Assessment',
            value: TabName.RISK_ASSESSMENT,
            active: currentPath === 'risk-assessment',
            navigateTo: withQueryParams(
              `${MainRoutes.JmsManagement}/booking/${bookingId}/risk-assessment`,
            ),
            disabled: !isBookingSubmitted,
            hidden: !isBookingSubmitted || isInmateContext,
          },
          {
            title: 'Legal',
            value: TabName.LEGAL,
            active: currentPath === 'legal',
            navigateTo: withQueryParams(
              `${MainRoutes.JmsManagement}/booking/${bookingId}/legal`,
            ),
            disabled: !isBookingSubmitted,
            hidden: !isBookingSubmitted || isInmateContext,
          },
          {
            title: 'Housing',
            value: TabName.HOUSING,
            active: currentPath === 'housing',
            navigateTo: withQueryParams(
              `${MainRoutes.JmsManagement}/booking/${bookingId}/housing`,
            ),
            disabled: !isBookingSubmitted,
            hidden: !isBookingSubmitted || isInmateContext,
          },
        ],
      },
    },
    {
      matchRoutes: [MainRoutes.JmsManagement, JmsManagementRoutes.Bookings],
      config: {
        headerTitle: 'Management',
        tabs: [
          {
            title: 'Inmates',
            value: 'inmates' as TabName,
            active: location.pathname === JmsManagementRoutes.Inmates,
            navigateTo: JmsManagementRoutes.Inmates,
            disabled: false,
          },
          {
            title: 'Bookings',
            value: 'bookings' as TabName,
            active: location.pathname === JmsManagementRoutes.Bookings,
            navigateTo: JmsManagementRoutes.Bookings,
            disabled: false,
          },
        ],
      },
    },
  ];
};
